import type { Row, Table } from '@tanstack/react-table';
import { addedDiff, deletedDiff, updatedDiff } from 'deep-object-diff';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { TableStore } from '../table-store';
import { TableDataState } from '../table-types';

function cloneRows<TData>(rows: readonly TData[]): TData[] {
  return rows ? JSON.parse(JSON.stringify(rows)) : [];
}

export type UseTableEditingOptions<TData extends object> = {
  tableId: string;
  defaultData: readonly TData[];
  tableStore?: TableStore | null;
  updateData?: ((rowIndex: number, columnId: string, value: unknown, row: Row<TData>) => void) | null;
  onBeforeEditRow?: ((row: TData) => Promise<boolean>) | null;
  onBeforeNewRow?: (() => Promise<boolean>) | null;
  onBeforeSaveRow?:
    | ((state: TableDataState, row: TData, changes: Partial<TData>) => Promise<boolean>)
    | null;
  onBeforeDeleteRow?: ((row: TData) => Promise<boolean>) | null;
  onSaveRow?:
    | ((rowIdx: number, row: TData, changedValues: Partial<TData>) => Promise<boolean | unknown>)
    | null;
  onNewRow?: (() => TData | Partial<TData>) | null;
  deleteRow?: ((row: TData, index: number) => Promise<boolean>) | null;
};

/**
 * Inline row editing state and handlers for {@link TableEditableV8}.
 * Returns `tableMeta` to pass into TanStack `useReactTable({ meta })`.
 */
export function useTableEditing<TData extends object>(options: UseTableEditingOptions<TData>) {
  const {
    tableId,
    defaultData,
    tableStore = null,
    updateData: onUpdateDataProp = null,
    onBeforeEditRow = null,
    onBeforeNewRow = null,
    onBeforeSaveRow = null,
    onBeforeDeleteRow = null,
    onSaveRow = null,
    onNewRow = null,
    deleteRow: onDeleteRowProp = null,
  } = options;

  const tableRef = useRef<Table<TData> | null>(null);
  const [data, setData] = useState<TData[]>(() => cloneRows(defaultData));
  const [originalData, setOriginalData] = useState<TData[]>(() => cloneRows(defaultData));
  const [editedRows, setEditedRows] = useState<Record<string, boolean>>({});
  const [dataState, setDataStateOrig] = useState(TableDataState.browse);

  const bindTable = useCallback((table: Table<TData>) => {
    tableRef.current = table;
  }, []);

  const setDataState = useCallback(
    (value: TableDataState) => {
      setDataStateOrig(value);
      if (tableStore) {
        tableStore.setState(value);
        if (tableStore.tableName !== tableId) tableStore.setTableName(tableId);
        if (tableRef.current) tableStore.setTable(tableRef.current);
      }
    },
    [tableId, tableStore],
  );

  useEffect(() => {
    const nextData = cloneRows(defaultData);
    const nextDataSignature = JSON.stringify(nextData);

    setData((prevData) =>
      JSON.stringify(prevData) === nextDataSignature ? prevData : nextData,
    );
    setOriginalData((prevData) =>
      JSON.stringify(prevData) === nextDataSignature ? prevData : nextData,
    );
    setEditedRows((prevEditedRows) =>
      Object.keys(prevEditedRows).length === 0 ? prevEditedRows : {},
    );
    setDataStateOrig(TableDataState.browse);

    if (tableStore) {
      if (tableStore.state !== TableDataState.browse) {
        tableStore.setState(TableDataState.browse);
      }
      if (tableStore.tableName !== tableId) {
        tableStore.setTableName(tableId);
      }
      if (tableStore.activeRowIdx !== undefined) {
        tableStore.setActiveRowIdx(undefined);
      }
    }
  }, [defaultData, tableId, tableStore]);

  const doUpdateData = useCallback(
    (rowIndex: number, columnId: string, value: unknown, row: Row<TData>) => {
      setData((old) =>
        old.map((rowItem, index) => {
          if (index !== rowIndex) return rowItem;

          if (columnId.indexOf('.') > 0) {
            const [frsPart, secPart] = columnId.split('.');
            const current = old[rowIndex] as Record<string, unknown>;
            return {
              ...current,
              [frsPart]: {
                ...(current[frsPart] as Record<string, unknown>),
                [secPart]: value,
              },
            } as TData;
          }

          return { ...old[rowIndex], [columnId]: value } as TData;
        }),
      );

      if (onUpdateDataProp) onUpdateDataProp(rowIndex, columnId, value, row);
    },
    [onUpdateDataProp],
  );

  const doRevertData = useCallback(
    (rowIndex: number, revert: boolean) => {
      if (revert) {
        setData((old) =>
          old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row)),
        );
      } else {
        setOriginalData((old) =>
          old.map((row, index) => (index === rowIndex ? data[rowIndex] : row)),
        );
      }
    },
    [data, originalData],
  );

  const removeRow = useCallback((rowIndex: number) => {
    const idx = parseInt(rowIndex.toString(), 10);
    const filterRows = (old: TData[]) => old.filter((_row, index) => index !== idx);
    setData(filterRows);
    setOriginalData(filterRows);
  }, []);

  const getEditingRowIdx = useCallback(() => {
    for (const key of Object.keys(editedRows)) {
      return parseInt(key, 10);
    }
    return undefined;
  }, [editedRows]);

  const doUpdateRow = useCallback(
    async (rowIndex: number) => {
      const originalRow = { ...originalData[rowIndex] };
      const changes = {
        ...updatedDiff(originalRow, data[rowIndex]),
        ...addedDiff(originalRow, data[rowIndex]),
        ...deletedDiff(originalRow, data[rowIndex]),
      } as Partial<TData>;

      for (const key of Object.keys(changes)) {
        const value = changes[key as keyof TData];
        if (value !== null && typeof value === 'object') {
          delete changes[key as keyof TData];
        }
      }

      if (onBeforeSaveRow) {
        const res = await onBeforeSaveRow(dataState, data[rowIndex], changes);
        if (!res) return false;
      }

      let res: boolean | unknown = true;
      if (onSaveRow) {
        res = await onSaveRow(rowIndex, data[rowIndex], changes);
        if (!res) return false;
      }

      setOriginalData(cloneRows(data));
      return res;
    },
    [data, dataState, onBeforeSaveRow, onSaveRow, originalData],
  );

  const doAddNewRow = useCallback(async () => {
    if (onBeforeNewRow) {
      const res = await onBeforeNewRow();
      if (!res) return;
    }

    const newRow = onNewRow ? onNewRow() : ({} as Partial<TData>);
    const newRowIdx = tableRef.current?.getRowModel().rows.length ?? data.length;
    const appendRow = (old: TData[]) => [...old, cloneRows([newRow as TData])[0]];

    setData(appendRow);
    setOriginalData(appendRow);
    setEditedRows((old) => ({ ...old, [newRowIdx]: !old[newRowIdx] }));
    setDataState(TableDataState.insert);

    if (tableStore && !tableStore.activeRowIdx) {
      tableStore.setActiveRowIdx(newRowIdx);
    }
  }, [data.length, onBeforeNewRow, onNewRow, setDataState, tableStore]);

  const doEditRow = useCallback(
    async (rowId: string | number) => {
      if (rowId === undefined) return false;

      const rowKey = String(rowId);
      const anotherRowIsEditing = Object.keys(editedRows).some((key) => key !== rowKey);
      if (anotherRowIsEditing) return false;

      const rowIndex = typeof rowId === 'number' ? rowId : parseInt(rowKey, 10);
      if (onBeforeEditRow) {
        const res = await onBeforeEditRow(data[rowIndex]);
        if (!res) return false;
      }

      setEditedRows((old) => ({ ...old, [rowKey]: !old[rowKey] }));
      setDataState(TableDataState.edit);
      return true;
    },
    [data, editedRows, onBeforeEditRow, setDataState],
  );

  const doCancelRow = useCallback(
    (rowIdx?: number) => {
      let targetIdx = rowIdx;
      if (targetIdx === undefined) {
        if ([TableDataState.insert, TableDataState.edit].includes(dataState)) {
          targetIdx = getEditingRowIdx();
        }
        if (targetIdx === undefined) return;
      }

      if (dataState === TableDataState.insert) removeRow(targetIdx);
      else doRevertData(targetIdx, true);

      setEditedRows({});
      setDataState(TableDataState.browse);

      if (dataState === TableDataState.insert && tableStore?.activeRowIdx === targetIdx) {
        tableStore.setActiveRowIdx(undefined);
      }
    },
    [dataState, doRevertData, getEditingRowIdx, removeRow, setDataState, tableStore],
  );

  const doSaveRow = useCallback(
    async (rowIdx?: number) => {
      let targetIdx = rowIdx ?? getEditingRowIdx();
      if (targetIdx === undefined) return false;

      const res = await doUpdateRow(targetIdx);
      if (!res) return false;

      setEditedRows({});
      setDataState(TableDataState.browse);
      return res;
    },
    [doUpdateRow, getEditingRowIdx, setDataState],
  );

  const doDeleteRow = useCallback(
    async (rowIdx?: number) => {
      const rowIndex = rowIdx ?? tableStore?.activeRowIdx ?? undefined;
      if (rowIndex === undefined) return;

      if (onBeforeDeleteRow) {
        const res = await onBeforeDeleteRow(data[rowIndex]);
        if (!res) return false;
      }

      if (onDeleteRowProp) await onDeleteRowProp(data[rowIndex], rowIndex);
      setDataState(TableDataState.browse);
      return true;
    },
    [data, onBeforeDeleteRow, onDeleteRowProp, setDataState, tableStore],
  );

  const getRow = useCallback(
    (rowIdx?: number) => {
      const rowIndex = rowIdx ?? tableStore?.activeRowIdx ?? undefined;
      if (rowIndex === undefined) return undefined;
      return data[rowIndex];
    },
    [data, tableStore],
  );

  const setDataValue = useCallback(
    (newValue: Partial<TData>) => {
      const rowIndex = tableStore?.activeRowIdx;
      if (rowIndex === undefined) return;
      setData((old) =>
        old.map((row, index) =>
          index === rowIndex ? ({ ...data[rowIndex], ...newValue } as TData) : row,
        ),
      );
    },
    [data, tableStore],
  );

  const tableMeta = {
    dataState,
    updateData: doUpdateData,
    updateRow: doUpdateRow,
    addNewRow: doAddNewRow,
    deleteRow: doDeleteRow,
    editedRows,
    setEditedRows,
    editRow: doEditRow,
    onBeforeEditRow,
    onBeforeNewRow,
    saveRow: doSaveRow,
    cancelRow: doCancelRow,
    revertData: doRevertData,
    getRow,
    setDataValue,
  };

  return {
    data,
    dataState,
    editedRows,
    tableMeta,
    bindTable,
  };
}
